import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { incrementGoal } from './useGoals';

export interface ActivityEntry {
  id: string;
  activity_type: string;
  duration_minutes: number | null;
  calories_burned: number;
  notes: string | null;
  logged_at: string;
}

export function useTodayActivity() {
  const { profile } = useUserStore();
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const totalCalories = activities.reduce((sum, a) => sum + (a.calories_burned || 0), 0);
  
  async function fetch() {
    if (!profile?.id) return;
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    
    const { data } = await supabase
      .from('activity_log')
      .select('*')
      .eq('user_id', profile.id)
      .gte('logged_at', today)
      .lt('logged_at', tomorrowStr)
      .order('logged_at', { ascending: false });

    setActivities((data as ActivityEntry[]) || []);
    setLoading(false);
  }
  
  useEffect(() => {
    fetch();
  }, [profile?.id]);

  return { activities, totalCalories, loading, refetch: fetch };
}

/**
 * Добавляет тренировку И увеличивает счётчик цели + начисляет XP + проверяет ачивки.
 */
export async function addActivity(
  userId: string,
  data: {
    activity_type: string;
    duration_minutes?: number;
    calories_burned: number;
    notes?: string;
    logged_at?: string;
  }
): Promise<{
  new_milestone: number | null;
  current_value: number;
  target_value: number;
} | null> {
  // 1. Сохраняем тренировку
  const insertData: any = {
    user_id: userId,
    activity_type: data.activity_type,
    duration_minutes: data.duration_minutes,
    calories_burned: data.calories_burned,
    notes: data.notes,
  };
  
  if (data.logged_at) {
    insertData.logged_at = data.logged_at;
  }
  
  const { error } = await supabase.from('activity_log').insert(insertData);
  if (error) throw error;
  
  // 2. Увеличиваем счётчик годовой цели «workouts_year»
  let goalResult = null;
  try {
    const result = await incrementGoal(userId, 'workouts_year', 1);
    if (result) {
      goalResult = {
        new_milestone: result.new_milestone,
        current_value: result.current_value,
        target_value: result.target_value,
      };
    }
  } catch (e) {
    console.error('[addActivity] increment goal error:', e);
  }
  
  // 3. Геймификация — асинхронно
  awardActivityGamification(userId).catch(err => {
    console.error('[addActivity] gamification error:', err);
  });
  
  return goalResult;
}

/**
 * 🆕 Обновляет существующую тренировку.
 * НЕ добавляет XP повторно, НЕ увеличивает счётчик целей.
 * Только корректировка данных.
 */
export async function updateActivity(
  id: string,
  data: {
    activity_type?: string;
    duration_minutes?: number | null;
    calories_burned?: number;
    notes?: string | null;
  }
): Promise<void> {
  // Убираем undefined чтобы не перезаписывать поля
  const updateData: any = {};
  if (data.activity_type !== undefined) updateData.activity_type = data.activity_type;
  if (data.duration_minutes !== undefined) updateData.duration_minutes = data.duration_minutes;
  if (data.calories_burned !== undefined) updateData.calories_burned = data.calories_burned;
  if (data.notes !== undefined) updateData.notes = data.notes;
  
  const { error } = await supabase
    .from('activity_log')
    .update(updateData)
    .eq('id', id);
  
  if (error) throw error;
}

// Геймификация после добавления активности
async function awardActivityGamification(userId: string) {
  try {
    // 1. +10 XP за тренировку
    await supabase.rpc('add_xp', { uid: userId, amount: 10 });
    
    // 2. Считаем общее количество тренировок
    const { count } = await supabase
      .from('activity_log')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    const totalWorkouts = count || 0;
    
    // 3. Проверяем ачивки за тренировки
    await supabase.rpc('check_achievement', { 
      uid: userId, 
      ach_id: 'first_workout', 
      new_progress: totalWorkouts 
    });
    
    if (totalWorkouts >= 10) {
      await supabase.rpc('check_achievement', { 
        uid: userId, 
        ach_id: 'workouts_10', 
        new_progress: totalWorkouts 
      });
    }
    
    if (totalWorkouts >= 50) {
      await supabase.rpc('check_achievement', { 
        uid: userId, 
        ach_id: 'workouts_50', 
        new_progress: totalWorkouts 
      });
    }
  } catch (e) {
    console.error('[awardActivityGamification] error:', e);
  }
}

export async function deleteActivity(id: string) {
  const { error } = await supabase.from('activity_log').delete().eq('id', id);
  if (error) throw error;
}
